import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Folder,
  Plus,
  Edit2,
  Trash2,
  Palette,
  Check,
  Star,
  Search,
  Loader2,
  Share2,
  Eye,
  Users,
} from 'lucide-react';
import { toast } from 'sonner';
import { useSearchParams } from 'react-router-dom';
import { PageLayout } from '@/components/layout/PageLayout';
import { ConfirmDialog, Modal } from '@/components/ui/Modal';
import { ShareResourceModal } from '@/components/collaboration/ShareResourceModal';
import { ProjectCollaboratorsPanel } from '@/components/collaboration/ProjectCollaboratorsPanel';
import { shareInviteService } from '@/services/shareInvites';
import {
  PROJECT_COLOR_OPTIONS,
  projectColorsService,
  hexWithAlpha,
  colorNameFromHex,
  type ProjectColor,
} from '@/services/projectColors';
import { tabService } from '@/services/tabs';
import { useTabMonitorSessionOptional } from '@/context/TabMonitorSessionContext';

export const ProjectColorPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [projects, setProjects] = useState<ProjectColor[]>([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedProject, setSelectedProject] = useState<ProjectColor | null>(null);
  const [shareTarget, setShareTarget] = useState<ProjectColor | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [projectFilter, setProjectFilter] = useState<'all' | 'mine' | 'shared'>('all');
  const [loading, setLoading] = useState(true);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);
  const [collaboratorsRefreshKey, setCollaboratorsRefreshKey] = useState(0);

  const fetchProjects = useCallback(async () => {
    setLoading(true);
    try {
      const list = await projectColorsService.list();
      setProjects(list);
    } catch (error) {
      console.error('Failed to fetch projects:', error);
      toast.error('Failed to load projects');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchProjects();
  }, [fetchProjects]);

  useEffect(() => {
    const acceptId = searchParams.get('accept-share');
    if (!acceptId) return;
    void (async () => {
      try {
        const inv = await shareInviteService.accept(acceptId);
        toast.success(`You now have access to "${inv.resource_name}"`);
        void fetchProjects();
      } catch (err) {
        toast.error((err as Error)?.message ?? 'Failed to accept invitation');
      } finally {
        const next = new URLSearchParams(searchParams);
        next.delete('accept-share');
        setSearchParams(next, { replace: true });
      }
    })();
  }, [searchParams, setSearchParams, fetchProjects]);

  const createProject = async (data: {
    name: string;
    description?: string;
    colorHex: string;
  }) => {
    try {
      const created = await projectColorsService.create(data);
      setProjects((prev) => [...prev, created]);
      setShowCreateModal(false);
      toast.success(`Created project "${created.name}"`);
    } catch (error) {
      console.error('Failed to create project:', error);
      toast.error('Failed to create project');
    }
  };

  const updateProject = async (
    id: string,
    data: { name: string; description?: string; colorHex: string; tabIds?: string[] }
  ) => {
    try {
      const updated = await projectColorsService.update(id, data);
      setProjects((prev) =>
        prev.map((p) =>
          p.id === id
            ? { ...updated, tabCount: data.tabIds?.length ?? updated.tabCount }
            : p
        )
      );
      setShowEditModal(false);
      setSelectedProject(null);
      toast.success('Project updated');
      void fetchProjects();
    } catch (error) {
      console.error('Failed to update project:', error);
      toast.error('Failed to update project');
    }
  };

  const deleteProject = (id: string) => {
    setSelectedProjectId(id);
    setShowDeleteConfirm(true);
  };

  const confirmDeleteProject = async () => {
    if (!selectedProjectId) return;
    try {
      await projectColorsService.delete(selectedProjectId);
      setProjects((prev) => prev.filter((p) => p.id !== selectedProjectId));
      toast.success('Project deleted');
    } catch (error) {
      console.error('Failed to delete project:', error);
      toast.error('Failed to delete project');
    }
    setShowDeleteConfirm(false);
    setSelectedProjectId(null);
  };

  const filteredProjects = projects.filter((p) => {
    const matchesSearch =
      p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesFilter =
      projectFilter === 'all' ||
      (projectFilter === 'shared' ? p.isShared === true : !p.isShared);
    return matchesSearch && matchesFilter;
  });

  const ownedCount = projects.filter((p) => !p.isShared).length;
  const sharedCount = projects.filter((p) => p.isShared).length;

  const openProjectView = (project: ProjectColor) => {
    setSelectedProject(project);
    setShowViewModal(true);
  };

  const openProjectEdit = (project: ProjectColor) => {
    setSelectedProject(project);
    setShowEditModal(true);
  };

  const totalTabs = projects.reduce((sum, p) => sum + (p.tabCount ?? 0), 0);
  const colorsUsed = new Set(projects.map((p) => p.colorHex)).size;

  return (
    <PageLayout
      title="Project Colors"
      description="Organize projects with custom colors and tags"
      icon={Palette}
    >
      <div className="space-y-6">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass-card shadow-lg p-8 border border-khiimori-border"
        >
          <div className="flex items-center justify-between mb-6">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setShowCreateModal(true)}
              className="px-6 py-3 bg-gradient-to-r from-pink-600 to-rose-600 text-white rounded-xl font-semibold hover:from-pink-700 hover:to-rose-700 transition-all shadow-lg flex items-center gap-2"
            >
              <Plus className="w-5 h-5" />
              New Project
            </motion.button>
          </div>

          <div className="relative mb-4">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-khiimori-text-muted" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search projects..."
              className="w-full pl-12 pr-4 py-3 border-2 border-khiimori-border rounded-xl bg-khiimori-bg-elevated/50 text-khiimori-text-primary focus:border-pink-500 focus:outline-none"
            />
          </div>

          <div className="flex flex-wrap gap-2 mb-6">
            {([
              ['all', 'All'],
              ['mine', 'Mine'],
              ['shared', 'Shared with me'],
            ] as const).map(([key, label]) => (
              <button
                key={key}
                type="button"
                onClick={() => setProjectFilter(key)}
                className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                  projectFilter === key
                    ? 'bg-primary-500/20 text-primary-200 border border-primary-500/40'
                    : 'bg-khiimori-bg-elevated text-khiimori-text-muted border border-khiimori-border hover:text-khiimori-text-primary'
                }`}
              >
                {label}
                {key === 'mine' && ownedCount > 0 ? ` (${ownedCount})` : ''}
                {key === 'shared' && sharedCount > 0 ? ` (${sharedCount})` : ''}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { label: 'Total Projects', value: projects.length },
              { label: 'Total Tabs', value: totalTabs },
              { label: 'Colors Used', value: colorsUsed },
              {
                label: 'Default Project',
                value: projects.filter((p) => p.isDefault).length,
              },
            ].map((stat) => (
              <div
                key={stat.label}
                className="p-4 rounded-xl bg-khiimori-bg-elevated border border-khiimori-border"
              >
                <div className="text-khiimori-text-secondary text-sm font-medium mb-1">
                  {stat.label}
                </div>
                <div className="text-3xl font-bold text-khiimori-text-primary">
                  {stat.value}
                </div>
              </div>
            ))}
          </div>
        </motion.div>

        {loading ? (
          <div className="flex items-center justify-center py-16 text-khiimori-text-secondary">
            <Loader2 className="w-8 h-8 animate-spin mr-3" />
            Loading projects...
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <AnimatePresence mode="popLayout">
              {filteredProjects.map((project, index) => (
                <motion.div
                  key={project.id}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  transition={{ delay: index * 0.05 }}
                  className="glass-card shadow-md overflow-hidden border border-khiimori-border hover:shadow-lg transition-all h-full flex flex-col"
                >
                  <div
                    className="h-16 p-4"
                    style={{ backgroundColor: hexWithAlpha(project.colorHex) }}
                  >
                    <div className="flex items-center justify-between">
                      <div
                        className="w-12 h-12 rounded-xl flex items-center justify-center shadow-sm"
                        style={{ backgroundColor: project.colorHex }}
                      >
                        <Folder className="w-6 h-6 text-white" />
                      </div>
                      {project.isDefault && (
                        <span className="px-2 py-1 bg-khiimori-bg-elevated/80 rounded-full text-xs font-medium flex items-center gap-1">
                          <Star className="w-3 h-3" />
                          Default
                        </span>
                      )}
                      {project.isShared && (
                        <span className="px-2 py-1 bg-blue-500/15 text-blue-300 rounded-full text-xs font-medium">
                          Shared with you
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="p-6 flex flex-col flex-1">
                    <h3 className="text-xl font-bold text-khiimori-text-primary mb-2">
                      {project.name}
                    </h3>
                    <p className="text-sm text-khiimori-text-secondary mb-4 min-h-[2.5rem]">
                      {project.description || 'No description'}
                    </p>

                    <div className="flex items-center gap-4 mb-3">
                      <span
                        className="text-2xl font-bold"
                        style={{ color: project.colorHex }}
                      >
                        {project.tabCount ?? 0}
                      </span>
                      <span className="text-sm text-khiimori-text-muted">
                        tabs assigned
                      </span>
                    </div>

                    <div className="flex items-center gap-1.5 text-xs text-khiimori-text-muted mb-4 min-h-[1.25rem]">
                      {(project.collaboratorCount ?? 0) > 1 ? (
                        <>
                          <Users className="w-3.5 h-3.5 shrink-0" />
                          {project.collaboratorCount} people with access
                        </>
                      ) : null}
                    </div>

                    <div className="flex items-center gap-2 mt-auto">
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => openProjectView(project)}
                        className="flex-1 px-3 py-2 bg-primary-500/10 text-primary-300 rounded-lg hover:bg-primary-500/20 transition-colors flex items-center justify-center gap-2"
                      >
                        <Eye className="w-4 h-4" />
                        View
                      </motion.button>
                      {!project.isShared && (
                        <>
                          <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => setShareTarget(project)}
                            className="px-3 py-2 bg-khiimori-bg-elevated text-khiimori-text-secondary rounded-lg hover:bg-khiimori-bg-elevated/80 transition-colors flex items-center justify-center"
                            title="Share project"
                          >
                            <Share2 className="w-4 h-4" />
                          </motion.button>
                          <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => openProjectEdit(project)}
                            className="px-3 py-2 bg-khiimori-bg-elevated text-khiimori-text-secondary rounded-lg hover:bg-khiimori-bg-elevated/80 transition-colors flex items-center justify-center"
                            title="Edit project"
                          >
                            <Edit2 className="w-4 h-4" />
                          </motion.button>
                          <button
                            type="button"
                            onClick={() => deleteProject(project.id)}
                            className="p-2 bg-red-500/10 text-red-400 rounded-lg hover:bg-red-500/20 transition-colors"
                            title="Delete project"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        )}

        {!loading && filteredProjects.length === 0 && (
          <div className="glass-card shadow-md p-12 text-center border border-khiimori-border">
            <Folder className="w-16 h-16 text-khiimori-text-secondary mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-khiimori-text-primary mb-2">
              {searchQuery || projectFilter !== 'all' ? 'No matching projects' : 'No projects yet'}
            </h3>
            <p className="text-khiimori-text-secondary mb-6">
              {searchQuery
                ? 'Try a different search term'
                : projectFilter === 'shared'
                  ? 'No projects have been shared with you yet.'
                  : projectFilter === 'mine'
                    ? 'You have not created any projects yet.'
                    : 'Create your first project to start organizing tabs with colors'}
            </p>
            {!searchQuery && projectFilter !== 'shared' && (
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setShowCreateModal(true)}
                className="px-6 py-3 bg-gradient-to-r from-pink-600 to-rose-600 text-white rounded-xl font-semibold hover:from-pink-700 hover:to-rose-700 transition-all shadow-lg"
              >
                Create First Project
              </motion.button>
            )}
          </div>
        )}
      </div>

      <ProjectModal
        isOpen={showViewModal && Boolean(selectedProject)}
        onClose={() => {
          setShowViewModal(false);
          setSelectedProject(null);
        }}
        onSubmit={() => undefined}
        mode="view"
        initialData={selectedProject ?? undefined}
        collaboratorsRefreshKey={collaboratorsRefreshKey}
        canManageCollaborators={!selectedProject?.isShared}
        onInviteCollaborators={() => selectedProject && setShareTarget(selectedProject)}
      />

      <ProjectModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSubmit={createProject}
        mode="create"
      />

      <ProjectModal
        isOpen={showEditModal && Boolean(selectedProject)}
        onClose={() => {
          setShowEditModal(false);
          setSelectedProject(null);
          void fetchProjects();
        }}
        onSubmit={(data) =>
          selectedProject && updateProject(selectedProject.id, data)
        }
        mode="edit"
        initialData={selectedProject ?? undefined}
        collaboratorsRefreshKey={collaboratorsRefreshKey}
        canManageCollaborators={!selectedProject?.isShared}
        onInviteCollaborators={() => selectedProject && setShareTarget(selectedProject)}
      />

      <ShareResourceModal
        isOpen={Boolean(shareTarget)}
        onClose={() => setShareTarget(null)}
        resourceType="project"
        resourceId={shareTarget?.id ?? ''}
        resourceName={shareTarget?.name ?? 'Project'}
        onShared={() => setCollaboratorsRefreshKey((k) => k + 1)}
      />

      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={confirmDeleteProject}
        title="Delete Project"
        message="Are you sure you want to delete this project? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
      />
    </PageLayout>
  );
};

interface ProjectModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: {
    name: string;
    description?: string;
    colorHex: string;
    tabIds?: string[];
  }) => void;
  mode: 'create' | 'edit' | 'view';
  initialData?: ProjectColor;
  collaboratorsRefreshKey?: number;
  canManageCollaborators?: boolean;
  onInviteCollaborators?: () => void;
}

const ProjectModal = ({
  isOpen,
  onClose,
  onSubmit,
  mode,
  initialData,
  collaboratorsRefreshKey = 0,
  canManageCollaborators = true,
  onInviteCollaborators,
}: ProjectModalProps) => {
  const session = useTabMonitorSessionOptional();
  const workspaceSessionId = session?.workspaceSessionId ?? null;

  const [name, setName] = useState(initialData?.name || '');
  const [description, setDescription] = useState(initialData?.description || '');
  const [colorHex, setColorHex] = useState(initialData?.colorHex || '#3B82F6');
  const [assignedTabIds, setAssignedTabIds] = useState<string[]>(
    initialData?.tabIds ?? []
  );
  const [sessionTabs, setSessionTabs] = useState<
    Array<{ id: string; title: string; url: string }>
  >([]);
  const [tabsLoading, setTabsLoading] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setName(initialData?.name || '');
    setDescription(initialData?.description || '');
    setColorHex(initialData?.colorHex || '#3B82F6');
    setAssignedTabIds(initialData?.tabIds ?? []);
  }, [isOpen, initialData]);

  const isView = mode === 'view';
  const isReadOnly = isView;

  useEffect(() => {
    if (!isOpen || mode === 'create' || !initialData?.id) return;

    let cancelled = false;
    void projectColorsService.get(initialData.id).then((project) => {
      if (!cancelled) {
        setAssignedTabIds(project.tabIds);
        if (isView) {
          setName(project.name);
          setDescription(project.description);
          setColorHex(project.colorHex);
        }
      }
    });
    return () => {
      cancelled = true;
    };
  }, [isOpen, mode, initialData?.id, isView]);

  useEffect(() => {
    if (!isOpen || isView || !workspaceSessionId) {
      if (!isView) setSessionTabs([]);
      return;
    }

    let cancelled = false;
    setTabsLoading(true);
    void tabService
      .getTabsBySession(workspaceSessionId, 1, 100)
      .then(({ tabs }) => {
        if (!cancelled) {
          setSessionTabs(
            tabs.map((t) => ({
              id: t.id,
              title: t.title || t.url,
              url: t.url,
            }))
          );
        }
      })
      .catch(() => {
        if (!cancelled) setSessionTabs([]);
      })
      .finally(() => {
        if (!cancelled) setTabsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen, workspaceSessionId, isView]);

  const toggleTab = async (tabId: string) => {
    if (!initialData?.id) return;
    const isAssigned = assignedTabIds.includes(tabId);
    try {
      if (isAssigned) {
        await projectColorsService.removeTab(initialData.id, tabId);
        setAssignedTabIds((prev) => prev.filter((id) => id !== tabId));
      } else {
        await projectColorsService.assignTab(initialData.id, tabId);
        setAssignedTabIds((prev) => [...prev, tabId]);
      }
    } catch (error) {
      console.error('Failed to update tab assignment:', error);
      toast.error('Failed to update tab assignment');
    }
  };

  const handleSubmit = () => {
    onSubmit({
      name,
      description,
      colorHex,
      tabIds: mode === 'edit' ? assignedTabIds : undefined,
    });
  };

  const modalTitle =
    mode === 'create' ? 'Create Project' : mode === 'edit' ? 'Edit Project' : 'Project Details';

  const actionFooter = (
    <div className="flex gap-3">
      {isView ? (
        <button
          type="button"
          onClick={onClose}
          className="flex-1 px-4 py-2 bg-gradient-to-r from-pink-600 to-rose-600 text-white rounded-lg hover:from-pink-700 hover:to-rose-700 transition-colors"
        >
          Close
        </button>
      ) : (
        <>
          <button
            type="button"
            onClick={onClose}
            className="flex-1 px-4 py-2 border border-khiimori-border text-khiimori-text-primary rounded-lg hover:bg-khiimori-bg-elevated/50 transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!name.trim()}
            className="flex-1 px-4 py-2 bg-gradient-to-r from-pink-600 to-rose-600 text-white rounded-lg hover:from-pink-700 hover:to-rose-700 transition-colors disabled:opacity-50"
          >
            {mode === 'create' ? 'Create' : 'Save'}
          </button>
        </>
      )}
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={modalTitle}
      size={mode === 'create' ? 'md' : 'lg'}
      scrollable={mode === 'edit' || mode === 'view'}
      footer={actionFooter}
    >
      <div className="space-y-4">
        {isView && initialData?.isShared && (
          <div className="rounded-lg border border-blue-500/30 bg-blue-500/10 px-3 py-2 text-sm text-blue-200">
            This project was shared with you. You can view details and assigned tabs, but only the owner can edit it.
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-khiimori-text-secondary mb-2">
            Project Name
          </label>
          {isReadOnly ? (
            <p className="text-khiimori-text-primary font-medium">{name || 'Untitled'}</p>
          ) : (
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter project name"
              className="w-full px-4 py-2 border border-khiimori-border rounded-lg bg-khiimori-bg-elevated/50 text-khiimori-text-primary focus:outline-none focus:ring-2 focus:ring-pink-500"
            />
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-khiimori-text-secondary mb-2">
            Description
          </label>
          {isReadOnly ? (
            <p className="text-sm text-khiimori-text-secondary whitespace-pre-wrap">
              {description || 'No description'}
            </p>
          ) : (
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Enter project description"
              rows={3}
              className="w-full px-4 py-2 border border-khiimori-border rounded-lg bg-khiimori-bg-elevated/50 text-khiimori-text-primary focus:outline-none focus:ring-2 focus:ring-pink-500"
            />
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-khiimori-text-secondary mb-2">
            Color
          </label>
          {isReadOnly ? (
            <div className="flex items-center gap-3">
              <div
                className="w-10 h-10 rounded-lg border border-khiimori-border"
                style={{ backgroundColor: colorHex }}
              />
              <span className="text-sm text-khiimori-text-primary">
                {colorNameFromHex(colorHex)} ({colorHex})
              </span>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-8 gap-2">
                {PROJECT_COLOR_OPTIONS.map((color) => {
                  const selected =
                    colorHex.toUpperCase() === color.hex.toUpperCase();
                  return (
                    <button
                      key={color.hex}
                      type="button"
                      title={color.name}
                      onClick={() => setColorHex(color.hex)}
                      className={`w-10 h-10 rounded-lg relative transition-all ${
                        selected
                          ? 'ring-2 ring-khiimori-text-primary ring-offset-2 ring-offset-khiimori-bg-elevated scale-110'
                          : 'hover:scale-105'
                      }`}
                      style={{ backgroundColor: color.hex }}
                    >
                      {selected && (
                        <Check className="w-5 h-5 text-white absolute inset-0 m-auto drop-shadow" />
                      )}
                    </button>
                  );
                })}
              </div>
              <p className="text-xs text-khiimori-text-muted mt-2">
                Selected: {colorNameFromHex(colorHex)} ({colorHex})
              </p>
            </>
          )}
        </div>

        {(mode === 'edit' || mode === 'view') && (
          <div>
            <label className="block text-sm font-medium text-khiimori-text-secondary mb-2">
              {isView ? 'Assigned tabs' : 'Assign tabs'}
            </label>
            {isView ? (
              <p className="text-sm text-khiimori-text-primary">
                <span className="text-2xl font-bold mr-2" style={{ color: colorHex }}>
                  {assignedTabIds.length}
                </span>
                tabs in this project
              </p>
            ) : !workspaceSessionId ? (
              <p className="text-sm text-khiimori-text-muted">
                Open Tab Monitor and sync tabs to assign them to this project.
              </p>
            ) : tabsLoading ? (
              <div className="flex items-center gap-2 text-sm text-khiimori-text-muted py-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                Loading workspace tabs...
              </div>
            ) : sessionTabs.length === 0 ? (
              <p className="text-sm text-khiimori-text-muted">
                No tabs in the current workspace session.
              </p>
            ) : (
              <div className="max-h-40 overflow-y-auto space-y-1 border border-khiimori-border rounded-lg p-2">
                {sessionTabs.map((tab) => {
                  const checked = assignedTabIds.includes(tab.id);
                  return (
                    <label
                      key={tab.id}
                      className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-khiimori-bg-elevated/50 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => void toggleTab(tab.id)}
                        className="rounded border-khiimori-border"
                      />
                      <span className="text-sm text-khiimori-text-primary truncate">
                        {tab.title}
                      </span>
                    </label>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {(mode === 'edit' || mode === 'view') && initialData?.id && (
          <ProjectCollaboratorsPanel
            projectId={initialData.id}
            canManage={canManageCollaborators}
            refreshKey={collaboratorsRefreshKey}
            isOwner={canManageCollaborators}
            onInviteClick={() => onInviteCollaborators?.()}
          />
        )}
      </div>
    </Modal>
  );
};

export default ProjectColorPage;
